import { Filter, TableComponentProps, TableData } from '../../models/table-models';
import { Params, useNavigate, useParams } from 'react-router-dom';
import { Button, Form, Spinner } from 'react-bootstrap';
import React, { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { getInventoryType, InventoryInner, inventoryTypeOrder } from './InventoryInner';
import { getAllInventories, useGetAllQuantities, useGetUserInventory, useGetWarehouses } from '../../utils/warehouses-hooks';
import { DataOrModifiedFn, resourceCache, useAsyncResource } from 'use-async-resource';
import { useTranslation } from "react-i18next";
import { useTableData } from '../../utils/data-hooks';
import { InventoryContent } from '../../models/warehouse-models';
import Select, { OnChangeValue } from 'react-select';
import { useGetUsers } from '../../utils/users-hooks';
import { PageSizeComponent } from '../../components/PageSizeComponent';
import XLSX from 'xlsx';
import FileSaver from 'file-saver';

interface UserInventoriesViewQueryParams extends Params {
    id?: string;
}

const ALL_USERS_OPTION_VALUE = 'all';
const ALL_INVENTORIES_PAGE_SIZE = 1000;

interface UserInventoryOption {
    label: string;
    value: string;
}

type AllInventoriesFilter = Filter & {
    forSale?: boolean;
};

const getAllInventoryPages = async (filter: AllInventoriesFilter): Promise<InventoryContent[]> => {
    const firstPage = await getAllInventories({
        'pager.page': 1,
        'pager.itemsPerPage': ALL_INVENTORIES_PAGE_SIZE,
        filter,
    });
    const backendPageSize = firstPage.pager.itemsPerPage || ALL_INVENTORIES_PAGE_SIZE;
    const pagesCount = firstPage.pager.pagesCount || Math.ceil(firstPage.pager.totalCount / backendPageSize);
    const allItems = [...firstPage.items];

    for (let page = 2; page <= pagesCount; page += 1) {
        const nextPage = await getAllInventories({
            'pager.page': page,
            'pager.itemsPerPage': backendPageSize,
            filter,
        });
        allItems.push(...nextPage.items);
    }

    return allItems;
};

const aggregateInventoryItems = (items: InventoryContent[]): InventoryContent[] => {
    const aggregatedItems: { [key: string]: InventoryContent } = {};

    items.forEach((item) => {
        const key = String(item.idOfPassport);

        if (!aggregatedItems[key]) {
            aggregatedItems[key] = {
                ...item,
                collectionOfItems: [item],
            };
            return;
        }

        const existingItem = aggregatedItems[key];
        if (existingItem.batchCode != null && item.batchCode && !existingItem.batchCode.includes(item.batchCode)) {
            existingItem.batchCode = existingItem.batchCode.concat(', ').concat(item.batchCode);
        }
        existingItem.count += item.count;
        existingItem.cost += item.cost;
        existingItem.availableAmount = (existingItem.availableAmount ?? 0) + (item.availableAmount ?? 0);
        existingItem.collectionOfItems?.push(item);
    });

    return Object.values(aggregatedItems);
};

const SingleUserInventoryTableLoader = ({
    filter,
    pagination,
    userId,
}: TableComponentProps & {
    userId: string;
}) => {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    const [forSale, setForSale] = useState(false);
    const inventoryFilter = useMemo(() => ({ ...filter, id: userId, ...(forSale ? { forSale } : {}) }), [filter, userId, forSale]);

    const [getInventory, fetchNewInventory] = useTableData<InventoryContent[]>(useGetUserInventory, pagination, inventoryFilter);

    const refetchInventoryHook = async () => {
        resourceCache(useGetWarehouses).clear();
        resourceCache(useGetUserInventory).clear();
        resourceCache(useGetAllQuantities).clear();

        await fetchNewInventory({
            'pager.page': pagination.currentPage,
            'pager.itemsPerPage': pagination.pageSize,
            pagination,
            filter: inventoryFilter,
        });
    };

    const changeOnlyForSell = (event: React.ChangeEvent<HTMLInputElement>) => {
        pagination.setCurrentPage(1);
        setForSale(event.target.checked);
    };

    return (
        <>
            <Form className='my-3'>
                <Form.Label htmlFor='isForSale' className='mx-2 mb-0'>
                    {t('showForSale')}
                </Form.Label>
                <Form.Check.Input
                    id='isForSale'
                    type='checkbox'
                    className='my-0 mx-1'
                    onChange={changeOnlyForSell}
                    defaultChecked={false}
                />
            </Form>
            <Suspense fallback={<Spinner animation='border' />}>
                <InventoryInner
                    getEntities={getInventory}
                    pagination={pagination}
                    refetchEntitiesHook={refetchInventoryHook}
                    userId={+userId}
                />
            </Suspense>
        </>
    );
};

const AllUserInventoriesTableLoader = ({
    filter,
    pagination,
    forSale,
    setForSale,
}: TableComponentProps & {
    forSale: boolean;
    setForSale: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    const inventoryFilter = useMemo(() => ({ ...filter, ...(forSale ? { forSale } : {}) }), [filter, forSale]);
    const [inventory, setInventory] = useState<TableData<InventoryContent[]>>({
        items: [],
        pager: {
            page: 1,
            itemsPerPage: pagination.pageSize,
            pagesCount: 0,
            totalCount: 0,
            itemsCount: 0,
        },
    });
    const [isLoading, setIsLoading] = useState(true);

    const fetchAllInventory = useCallback(async (): Promise<TableData<InventoryContent[]>> => {
        const allItems = await getAllInventoryPages(inventoryFilter);

        return {
            items: allItems,
            pager: {
                page: pagination.currentPage,
                itemsPerPage: pagination.pageSize,
                pagesCount: Math.ceil(allItems.length / pagination.pageSize),
                totalCount: allItems.length,
                itemsCount: allItems.length,
            },
        };
    }, [inventoryFilter, pagination.currentPage, pagination.pageSize]);

    useEffect(() => {
        let isActive = true;

        const loadInventory = async () => {
            setIsLoading(true);
            const nextInventory = await fetchAllInventory();

            if (!isActive) return;

            setInventory(nextInventory);
            setIsLoading(false);
        };

        loadInventory();

        return () => {
            isActive = false;
        };
    }, [fetchAllInventory]);

    const refetchInventoryHook = async () => {
        resourceCache(useGetWarehouses).clear();
        resourceCache(useGetUserInventory).clear();
        resourceCache(useGetAllQuantities).clear();

        setIsLoading(true);
        setInventory(await fetchAllInventory());
        setIsLoading(false);
    };

    const changeOnlyForSell = (event: React.ChangeEvent<HTMLInputElement>) => {
        pagination.setCurrentPage(1);
        setForSale(event.target.checked);
    };

    return (
        <>
            <Form className='my-3'>
                <Form.Label htmlFor='isForSale' className='mx-2 mb-0'>
                    {t('showForSale')}
                </Form.Label>
                <Form.Check.Input
                    id='isForSale'
                    type='checkbox'
                    className='my-0 mx-1'
                    onChange={changeOnlyForSell}
                    defaultChecked={false}
                />
            </Form>
            {isLoading ? (
                <Spinner animation='border' />
            ) : (
                <InventoryInner
                    getEntities={(() => inventory) as DataOrModifiedFn<TableData<InventoryContent[]>>}
                    pagination={pagination}
                    refetchEntitiesHook={refetchInventoryHook}
                    allowEdit={false}
                    showPagination={false}
                />
            )}
        </>
    );
};

export const UserInventoriesView = ({ filter, pagination, setFilter, appliedFilter, hasSearched, searchVersion }: TableComponentProps) => {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    const { t: tableT } = useTranslation('translation', { keyPrefix: 'table' });
    const params = useParams<UserInventoriesViewQueryParams>() as UserInventoriesViewQueryParams;
    const navigate = useNavigate();
    const [selectedUserId, setSelectedUserId] = useState<string | undefined>(params.id);
    const [allInventoriesSelected, setAllInventoriesSelected] = useState(false);
    const [allForSale, setAllForSale] = useState(false);
    const [isExportingAllInventories, setIsExportingAllInventories] = useState(false);
    const [getUsers] = useAsyncResource(useGetUsers, undefined, 'DISTRIBUTOR,INSTALLER');
    const { items: users } = getUsers();
    const userOptions = useMemo<UserInventoryOption[]>(
        () => [
            { label: t('all'), value: ALL_USERS_OPTION_VALUE },
            ...users.map((user) => ({
                label: user.name || user.username,
                value: String(user.id),
            })),
        ],
        [t, users]
    );
    const selectedUser = selectedUserId ? userOptions.find((option) => option.value === selectedUserId) ?? null : null;

    useEffect(() => {
        setSelectedUserId(params.id);
        if (params.id) {
            setAllInventoriesSelected(false);
        }
    }, [params.id]);

    const onUserChange = (option: OnChangeValue<UserInventoryOption, false>) => {
        const id = option?.value && option.value !== ALL_USERS_OPTION_VALUE ? option.value : undefined;
        pagination.setCurrentPage(1);
        setSelectedUserId(id);

        if (option?.value === ALL_USERS_OPTION_VALUE) {
            setAllInventoriesSelected(true);
            navigate('/inventories');
            return;
        }

        setAllInventoriesSelected(false);

        if (id) {
            navigate(`/inventories/${id}`);
            return;
        }

        navigate('/inventories');
    };

    const exportAllInventories = async () => {
        setIsExportingAllInventories(true);

        const allItems = await getAllInventoryPages({ ...filter, ...(allForSale ? { forSale: allForSale } : {}) });
        const aggregatedItems = aggregateInventoryItems(allItems).sort((firstItem, secondItem) => {
            const firstTypeIndex = inventoryTypeOrder.indexOf(getInventoryType(firstItem));
            const secondTypeIndex = inventoryTypeOrder.indexOf(getInventoryType(secondItem));

            if (firstTypeIndex !== secondTypeIndex) return firstTypeIndex - secondTypeIndex;
            return firstItem.passport.localeCompare(secondItem.passport);
        });
        const headers = [tableT('passport'), tableT('batches'), tableT('count'), tableT('availableAmount')];
        const formattedData = aggregatedItems.reduce<Array<Array<string | number>>>((rows, item, index) => {
            const type = getInventoryType(item);
            const previousItem = aggregatedItems[index - 1];

            if (!previousItem || getInventoryType(previousItem) !== type) {
                rows.push([type === 'UNKNOWN' ? String(t('withoutType')) : String(t(type)), '', '', '']);
            }

            rows.push([
                item.passport,
                item.batchCode,
                item.count,
                item.availableAmount ?? '',
            ]);

            return rows;
        }, []);
        const ws = XLSX.utils.aoa_to_sheet([headers, ...formattedData]);
        ws['!cols'] = [
            { wch: 40 },
            { wch: 45 },
            { wch: 12 },
            { wch: 18 },
        ];
        const wb = { Sheets: { data: ws }, SheetNames: ['data'] };
        const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const data = new Blob([excelBuffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
        });

        FileSaver.saveAs(data, `inventories-${new Date().toISOString().slice(0, 10)}.xlsx`);
        setIsExportingAllInventories(false);
    };

    return (
        <>
            <div className='w-100 d-flex justify-content-between align-items-stretch align-items-lg-center flex-column flex-lg-row gap-2 my-3'>
                {selectedUserId ? (
                    <PageSizeComponent pagination={pagination} itemsToShow={[5, 10, 20, 50, 75, 100]} />
                ) : allInventoriesSelected ? (
                    <Button
                        variant='warning'
                        className='mb-2'
                        onClick={exportAllInventories}
                        disabled={isExportingAllInventories}
                    >
                        {isExportingAllInventories ? <Spinner animation='border' size='sm' /> : 'Експорт в XLS'}
                    </Button>
                ) : (
                    <div />
                )}
                <Select<UserInventoryOption, false>
                    className='logs-select'
                    placeholder={t('distributor')}
                    isClearable
                    value={selectedUser}
                    options={userOptions}
                    getOptionLabel={(option) => option.label}
                    getOptionValue={(option) => option.value}
                    onChange={onUserChange}
                    backspaceRemovesValue
                />
            </div>
            {selectedUserId ? (
                <SingleUserInventoryTableLoader
                    filter={filter}
                    setFilter={setFilter}
                    pagination={pagination}
                    appliedFilter={appliedFilter}
                    hasSearched={hasSearched}
                    searchVersion={searchVersion}
                    userId={selectedUserId}
                />
            ) : allInventoriesSelected ? (
                <AllUserInventoriesTableLoader
                    filter={filter}
                    setFilter={setFilter}
                    pagination={pagination}
                    appliedFilter={appliedFilter}
                    hasSearched={hasSearched}
                    searchVersion={searchVersion}
                    forSale={allForSale}
                    setForSale={setAllForSale}
                />
            ) : null}
        </>
    );
};
